import { GridAdapter } from "./adapters/GridAdapter";
import { GridInstance, GridLayout, MAX_LAYOUTS, MIN_LAYOUTS } from "./models/GridInstance";
import { StringDevUIBuilder as GridUIBuilder } from "../core/ui/StringDevUIBuilder";
import { resolveDevtoolsIcon } from "../core/StringDevIconRegistry";
import {
  makeBadge,
  makeDeleteButton,
  makePanel,
  makePanelHeader,
  makePanelContent,
  makeIconButton,
  makePanelButton,
} from "../core/StringDevElements";

type DropPlacement = "before" | "after";

const MOBILE_PANEL_MARGIN = 12;
const MOBILE_PANEL_MAX_WIDTH = 360;
const MOBILE_PANEL_MAX_HEIGHT = 560;

export class GridHUD {
  private target: HTMLElement;
  private adapterRegistry: Map<string, GridAdapter>;
  private hudRoot: HTMLDivElement;
  private trigger: HTMLButtonElement;
  private panel: HTMLDivElement;
  private layoutTitle!: HTMLInputElement;

  private layoutChipTabs!: HTMLDivElement;
  private layoutSettingsToggle!: HTMLButtonElement;
  private layoutSettingsList!: HTMLDivElement;
  private layoutAddButton!: HTMLButtonElement;
  private layoutIOActions!: HTMLDivElement;
  private listContainer!: HTMLDivElement;
  private addGuideButton!: HTMLButtonElement;
  private headerAddButton!: HTMLButtonElement;
  private addPanel!: HTMLDivElement;
  private addGridList!: HTMLDivElement;
  private layoutPanel!: HTMLDivElement;
  private detailPanel!: HTMLDivElement;
  private detailContent!: HTMLDivElement;
  private detailTitle: HTMLSpanElement | null = null;
  private detailIcon: HTMLSpanElement | null = null;
  private detailBuilder: GridUIBuilder | null = null;
  private instances: GridInstance[] = [];
  private layouts: GridLayout[] = [];
  private panelTitleValue = "Layout";
  private selectedLayoutId = "";
  private resolvedLayoutId = "";
  private selectedInstanceId: string | null = null;
  private selectedBreakpointLayoutId: string | null = null;
  private lastLayoutChipsSignature: string | null = null;
  private lastLayoutSettingsSignature: string | null = null;
  private lastListSignature: string | null = null;
  private isOpen = false;
  private panelInBody = false;
  private isAddPanelOpen = false;
  private isLayoutSettingsOpen = false;
  private enabled = true;

  private onDocumentPointerDownBind!: (event: PointerEvent) => void;
  private onViewportChangeBind!: () => void;

  private onAdd!: (type: string) => void;
  private onRemove!: (id: string) => void;
  private onToggle!: (id: string) => void;
  private onSettingChange!: (id: string, key: string, value: any) => void;
  private onReorder!: (draggedId: string, targetId: string, placement: DropPlacement) => void;
  private onMoveToEnd!: (id: string) => void;
  private onSelectLayout!: (layoutId: string) => void;
  private onUpdateLayoutMinWidth!: (layoutId: string, minWidth: number) => void;
  private onAddLayout!: () => void;
  private onRemoveLayout!: (layoutId: string) => void;
  private onExport!: () => void;
  private onImport!: () => void;
  private onRenameInstance!: (instanceId: string, name: string) => void;
  private onRenamePanelTitle!: (title: string) => void;
  private onHUDOpen!: () => void;
  private onHUDClose!: () => void;
  private onLayoutPanelOpen!: () => void;
  private onLayoutPanelClose!: () => void;

  constructor(
    target: HTMLElement,
    adapterRegistry: Map<string, GridAdapter>,
    callbacks: {
      onAdd: (type: string) => void;
      onRemove: (id: string) => void;
      onToggle: (id: string) => void;
      onSettingChange: (id: string, key: string, value: any) => void;
      onReorder: (draggedId: string, targetId: string, placement: DropPlacement) => void;
      onMoveToEnd: (id: string) => void;
      onSelectLayout: (layoutId: string) => void;
      onUpdateLayoutMinWidth: (layoutId: string, minWidth: number) => void;
      onAddLayout: () => void;
      onRemoveLayout: (layoutId: string) => void;
      onExport: () => void;
      onImport: () => void;
      onRenameInstance: (instanceId: string, name: string) => void;
      onRenamePanelTitle: (title: string) => void;
      onHUDOpen: () => void;
      onHUDClose: () => void;
      onLayoutPanelOpen: () => void;
      onLayoutPanelClose: () => void;
    },
    options?: { attachTrigger?: boolean },
  ) {
    this.target = target;
    this.adapterRegistry = adapterRegistry;
    this.onAdd = callbacks.onAdd;
    this.onRemove = callbacks.onRemove;
    this.onToggle = callbacks.onToggle;
    this.onSettingChange = callbacks.onSettingChange;
    this.onReorder = callbacks.onReorder;
    this.onMoveToEnd = callbacks.onMoveToEnd;
    this.onSelectLayout = callbacks.onSelectLayout;
    this.onUpdateLayoutMinWidth = callbacks.onUpdateLayoutMinWidth;
    this.onAddLayout = callbacks.onAddLayout;
    this.onRemoveLayout = callbacks.onRemoveLayout;
    this.onExport = callbacks.onExport;
    this.onImport = callbacks.onImport;
    this.onRenameInstance = callbacks.onRenameInstance;
    this.onRenamePanelTitle = callbacks.onRenamePanelTitle;
    this.onHUDOpen = callbacks.onHUDOpen;
    this.onHUDClose = callbacks.onHUDClose;
    this.onLayoutPanelOpen = callbacks.onLayoutPanelOpen;
    this.onLayoutPanelClose = callbacks.onLayoutPanelClose;
    this.onDocumentPointerDownBind = this.onDocumentPointerDown.bind(this);
    this.onViewportChangeBind = () => this.syncPanelPlacement();

    this.hudRoot = this.createHudRoot();
    this.trigger = this.createTrigger();
    this.panel = this.createMainPanel();
    this.addPanel = this.createAddPanel();
    this.layoutPanel = this.createLayoutPanel();
    this.detailPanel = this.createDetailPanel();

    if (options?.attachTrigger !== false) {
      this.hudRoot.appendChild(this.trigger);
    }
    this.panel.appendChild(this.addPanel);
    this.panel.appendChild(this.layoutPanel);
    this.panel.appendChild(this.detailPanel);
    this.hudRoot.appendChild(this.panel);
    document.addEventListener("pointerdown", this.onDocumentPointerDownBind);
    window.addEventListener("resize", this.onViewportChangeBind);
    window.visualViewport?.addEventListener("resize", this.onViewportChangeBind);
    window.visualViewport?.addEventListener("scroll", this.onViewportChangeBind);
  }

  refresh(
    instances: GridInstance[],
    layouts: GridLayout[],
    panelTitle: string,
    selectedLayoutId: string,
    resolvedLayoutId: string,
  ): void {
    this.instances = [...instances];
    this.layouts = [...layouts];
    this.panelTitleValue = panelTitle.trim() || "Layout";
    this.selectedLayoutId = selectedLayoutId;
    this.resolvedLayoutId = resolvedLayoutId;
    if (
      this.selectedInstanceId &&
      !instances.some((instance) => instance.id === this.selectedInstanceId)
    ) {
      this.clearSelection();
    }
    if (
      this.selectedBreakpointLayoutId &&
      !this.layouts.some((layout) => layout.id === this.selectedBreakpointLayoutId)
    ) {
      this.selectedBreakpointLayoutId = null;
    }
    this.layoutTitle.value = this.panelTitleValue;
    this.renderLayoutChips();
    this.renderLayoutSettings();
    this.renderList();
    this.renderDetailPanel();
  }

  destroy(): void {
    this.detailBuilder?.destroy();
    document.removeEventListener("pointerdown", this.onDocumentPointerDownBind);
    window.removeEventListener("resize", this.onViewportChangeBind);
    window.visualViewport?.removeEventListener("resize", this.onViewportChangeBind);
    window.visualViewport?.removeEventListener("scroll", this.onViewportChangeBind);
    if (this.panelInBody) {
      this.panel.remove();
    }
    this.hudRoot.remove();
  }

  getTriggerElement(): HTMLButtonElement {
    return this.trigger;
  }

  getHudElement(): HTMLDivElement {
    return this.hudRoot;
  }

  setTargetId(targetId: string): void {
    this.hudRoot.setAttribute("data-target-id", targetId);
    this.trigger.setAttribute("data-target-id", targetId);
    this.panel.setAttribute("data-target-id", targetId);
    this.addPanel.setAttribute("data-target-id", targetId);
    this.layoutPanel.setAttribute("data-target-id", targetId);
    this.detailPanel.setAttribute("data-target-id", targetId);
  }

  setAnchorPosition(docX: number, docY: number, align: "top-left" | "top-right" = "top-left"): void {
    this.hudRoot.setAttribute("data-visible", "true");
    const anchorTransform = align === "top-right" ? " translate(-100%, 0%)" : "";
    this.hudRoot.style.transform = `translate3d(${Math.round(docX)}px, ${Math.round(docY)}px, 0)${anchorTransform}`;
    this.syncPanelPlacement();
  }

  setAnchorVisible(visible: boolean): void {
    this.hudRoot.setAttribute("data-visible", visible ? "true" : "false");
  }

  isHUDOpen(): boolean {
    return this.isOpen;
  }

  isLayoutSettingsPanelOpen(): boolean {
    return this.isLayoutSettingsOpen;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.hide();
    this.hudRoot.style.display = enabled ? "" : "none";
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  private createHudRoot(): HTMLDivElement {
    return makeBadge();
  }

  private createTrigger(): HTMLButtonElement {
    const trigger = makeIconButton({
      icon: "settings",
      label: "Open layout guides",
      modifiers: ["toggle"],
      attrs: {
        "data-devguides-id": "layout",
        "data-open": "false",
        "data-state": "visible",
        "data-active": "false",
        "aria-expanded": "false",
      },
    });
    trigger.addEventListener("click", (event) => {
      event.stopPropagation();
      this.toggle();
    });
    return trigger;
  }

  private createMainPanel(): HTMLDivElement {
    const panel = makePanel();

    const header = makePanelHeader();

    this.layoutTitle = document.createElement("input");
    this.layoutTitle.setAttribute("data-stdg-input", "");
    this.layoutTitle.setAttribute("data-stdg-panel-title", "");
    this.layoutTitle.type = "text";
    this.layoutTitle.value = this.panelTitleValue;
    this.layoutTitle.addEventListener("blur", () => {
      const nextTitle = this.layoutTitle.value.trim() || "Layout";
      if (nextTitle !== this.panelTitleValue) {
        this.panelTitleValue = nextTitle;
        this.onRenamePanelTitle(nextTitle);
      }
      this.layoutTitle.value = nextTitle;
    });
    this.layoutTitle.addEventListener("keydown", (event) => {
      event.stopPropagation();
      if (event.key === "Enter") this.layoutTitle.blur();
      if (event.key === "Escape") {
        this.layoutTitle.value = this.panelTitleValue.trim() || "Layout";
        this.layoutTitle.blur();
      }
    });
    this.layoutTitle.addEventListener("click", (event) => {
      event.stopPropagation();
      this.startLayoutTitleRename();
    });

    this.layoutSettingsToggle = makeIconButton({
      icon: "options",
      label: "Layout Settings",
      modifiers: ["toggle", "left"],
      attrs: { "data-active": "false" },
    });
    this.layoutSettingsToggle.addEventListener("click", (event) => {
      event.stopPropagation();
      if (this.isLayoutSettingsOpen) this.closeLayoutPanel();
      else this.openLayoutPanel();
    });

    const closeButton = makeIconButton({
      icon: "close",
      label: "Close layout guides",
      modifiers: ["right"],
    });
    closeButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.hide();
    });

    this.headerAddButton = makeIconButton({
      icon: "plus",
      label: "Add Layout Guide",
      modifiers: ["toggle", "left"],
      attrs: { "data-active": "false" },
    });
    this.headerAddButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.openAddPanel();
    });

    header.appendChild(this.layoutTitle);
    header.appendChild(this.headerAddButton);
    header.appendChild(closeButton);

    const responsive = document.createElement("div");
    responsive.setAttribute("data-stdg-panel-responsive", "");

    this.layoutChipTabs = document.createElement("div");
    this.layoutChipTabs.setAttribute("data-stdg-panel-list", "");

    responsive.appendChild(this.layoutSettingsToggle);
    responsive.appendChild(this.layoutChipTabs);

    this.listContainer = makePanelContent({ "data-string-grid-list": "" });

    this.addGuideButton = makePanelButton({
      icon: "plus",
      label: "Add Layout Guide",
      withLabel: true,
      attrs: { "data-active": "false" },
    });
    this.addGuideButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.openAddPanel();
    });

    this.listContainer.appendChild(this.addGuideButton);

    panel.appendChild(header);
    panel.appendChild(responsive);
    panel.appendChild(this.listContainer);
    return panel;
  }

  private createAddPanel(): HTMLDivElement {
    const panel = makePanel({ "data-panel": "add" });
    const header = makePanelHeader();

    const closeButton = makeIconButton({
      icon: "close",
      label: "Close panel",
      modifiers: ["right"],
    });
    closeButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.closeAddPanel();
    });
    header.appendChild(closeButton);

    this.addGridList = makePanelContent({
      "data-string-grid-add-grid-list": "",
      "data-stdg-panel-content-50": "",
    });
    this.renderAddPanelList();

    panel.appendChild(header);
    panel.appendChild(this.addGridList);
    return panel;
  }

  private renderAddPanelList(): void {
    this.addGridList.innerHTML = "";

    if (this.adapterRegistry.size === 0) {
      const empty = document.createElement("div");
      empty.setAttribute("data-string-grid-add-grid-empty", "");
      empty.textContent = "No layout guides available";
      this.addGridList.appendChild(empty);
      return;
    }

    for (const [, adapter] of this.adapterRegistry) {
      const item = makePanelButton({ icon: "", label: adapter.label, withLabel: true });
      item.innerHTML = `${adapter.icon.replace("<svg", "<svg data-stdg-icon-16")}<span>${adapter.label}</span>`;
      item.addEventListener("click", (event) => {
        event.stopPropagation();
        this.onAdd(adapter.type);
        this.closeAddPanel();
      });
      this.addGridList.appendChild(item);
    }
  }

  private createLayoutPanel(): HTMLDivElement {
    const panel = makePanel({ "data-panel": "layout" });
    const header = makePanelHeader();

    this.layoutAddButton = makeIconButton({
      icon: "plus",
      label: "Add Layout Guide",
      modifiers: ["toggle", "left"],
      attrs: { "data-active": "false" },
    });
    this.layoutAddButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.onAddLayout();
    });

    const closeButton = makeIconButton({
      icon: "close",
      label: "Close panel",
      modifiers: ["right"],
    });
    closeButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.closeLayoutPanel();
    });

    header.appendChild(this.layoutAddButton);
    header.appendChild(closeButton);

    this.layoutSettingsList = makePanelContent();

    this.layoutIOActions = makePanelContent({ "data-stdg-panel-content-50": "" });

    const exportButton = makePanelButton({
      icon: "export",
      label: "Export",
      withLabel: true,
      attrs: { "data-io": "export" },
    });
    exportButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.onExport();
    });

    const importButton = makePanelButton({
      icon: "import",
      label: "Import",
      withLabel: true,
      attrs: { "data-io": "import" },
    });
    importButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.onImport();
    });

    this.layoutIOActions.appendChild(exportButton);
    this.layoutIOActions.appendChild(importButton);

    const hr = document.createElement("span");
    hr.setAttribute("data-stdg-panel-hr", "");

    panel.appendChild(header);
    panel.appendChild(this.layoutSettingsList);
    panel.appendChild(hr);
    panel.appendChild(this.layoutIOActions);
    return panel;
  }

  private createDetailPanel(): HTMLDivElement {
    const panel = makePanel({ "data-panel": "detail" });
    const header = makePanelHeader();

    this.detailIcon = document.createElement("span");
    this.detailIcon.setAttribute("data-stdg-holder-icon-16", "");
    this.detailIcon.setAttribute("data-stdg-button-left", "");

    this.detailTitle = document.createElement("span");
    this.detailTitle.addEventListener("click", (event) => {
      event.stopPropagation();
      this.startDetailRename();
    });

    const closeButton = makeIconButton({
      icon: "close",
      label: "Close detail panel",
      modifiers: ["right"],
    });
    closeButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.closeDetailPanel();
    });

    header.appendChild(this.detailIcon);
    header.appendChild(this.detailTitle);
    header.appendChild(closeButton);

    this.detailContent = document.createElement("div");
    this.detailContent.setAttribute("data-string-dev-detail-body", "");

    panel.appendChild(header);
    panel.appendChild(this.detailContent);
    return panel;
  }
  private renderLayoutChips(): void {
    const signature = this.layouts
      .map((layout) =>
        [
          layout.id,
          layout.label,
          layout.minWidth,
          layout.id === this.selectedLayoutId,
          layout.id === this.resolvedLayoutId,
        ].join(":"),
      )
      .join("|");
    if (signature === this.lastLayoutChipsSignature) {
      this.layoutSettingsToggle.setAttribute("data-active", String(this.isLayoutSettingsOpen));
      return;
    }
    this.lastLayoutChipsSignature = signature;
    this.layoutChipTabs.innerHTML = "";
    const activateChipLayout = (layoutId: string) => {
      this.selectedLayoutId = layoutId;
      this.renderLayoutChips();
      this.renderLayoutSettings();
    };

    // Render S → XL order (smallest first, so reverse the descending array)
    [...this.layouts].reverse().forEach((layout) => {
      const chip = makePanelButton({ icon: "", label: `Select ${layout.label} layout` });
      chip.innerHTML = `<span>${layout.label}</span>`;
      chip.setAttribute("data-active", String(layout.id === this.selectedLayoutId));
      chip.setAttribute("data-resolved", String(layout.id === this.resolvedLayoutId));
      chip.setAttribute("data-mode", layout.label);
      chip.addEventListener("click", (event) => {
        event.stopPropagation();
        activateChipLayout(layout.id);
        this.onSelectLayout(layout.id);
      });
      this.layoutChipTabs.appendChild(chip);
    });
    this.layoutSettingsToggle.setAttribute("data-active", String(this.isLayoutSettingsOpen));
  }

  private renderLayoutSettings(): void {
    const signature = `${this.layouts
      .map((layout) => `${layout.id}:${layout.label}:${layout.minWidth}`)
      .join("|")}#${this.selectedBreakpointLayoutId ?? ""}`;
    if (signature === this.lastLayoutSettingsSignature) {
      this.layoutAddButton.style.display = this.layouts.length >= MAX_LAYOUTS ? "none" : "";
      return;
    }
    this.lastLayoutSettingsSignature = signature;
    this.layoutSettingsList.innerHTML = "";
    this.layoutAddButton.style.display = this.layouts.length >= MAX_LAYOUTS ? "none" : "";
    const { breakpointsRow, inputField } = this.createBreakpointWidget();
    this.layoutSettingsList.appendChild(breakpointsRow);
    this.layoutSettingsList.appendChild(inputField);
  }

  private createBreakpointWidget(): { breakpointsRow: HTMLDivElement; inputField: HTMLDivElement } {
    const orderedLayouts = [...this.layouts].reverse(); // S first
    const canDelete = orderedLayouts.length > MIN_LAYOUTS;

    const breakpointsRow = document.createElement("div");
    breakpointsRow.setAttribute("data-stdg-panel-breakpoints", "");

    const inputField = document.createElement("div");
    inputField.setAttribute("data-stdg-panel-field", "");

    const inputId = `st-dg-break-${Date.now()}`;

    const fieldIconWrap = document.createElement("span");
    fieldIconWrap.innerHTML = resolveDevtoolsIcon(16, "break", "second");

    const editorInput = document.createElement("input");
    editorInput.id = inputId;
    editorInput.type = "number";
    editorInput.inputMode = "numeric";
    editorInput.step = "1";
    editorInput.min = "1";
    editorInput.setAttribute("data-stdg-input", "");

    const pxLabel = document.createElement("label");
    pxLabel.htmlFor = inputId;
    pxLabel.setAttribute("data-stdg-field-input-disable", "");
    pxLabel.textContent = "px";

    inputField.appendChild(fieldIconWrap);
    inputField.appendChild(editorInput);
    inputField.appendChild(pxLabel);

    let activeLayoutId: string | null = null;
    let activeMarkerEl: HTMLButtonElement | null = null;
    let activeValue = 0;

    const setActive = (
      layoutId: string,
      currentValue: number,
      markerEl: HTMLButtonElement,
      focusInput = false,
    ) => {
      activeMarkerEl?.setAttribute("data-active", "false");
      activeLayoutId = layoutId;
      activeMarkerEl = markerEl;
      activeValue = currentValue;
      this.selectedBreakpointLayoutId = layoutId;
      markerEl.setAttribute("data-active", "true");
      editorInput.value = String(currentValue);
      if (focusInput) {
        editorInput.focus();
        editorInput.select();
      }
    };

    const commitEditor = () => {
      if (!activeLayoutId) return;
      if (!editorInput.isConnected) return;
      const parsed = Number.parseInt(editorInput.value, 10);
      if (Number.isFinite(parsed) && parsed >= 0) {
        if (parsed === activeValue) return;
        activeValue = parsed;
        this.onUpdateLayoutMinWidth(activeLayoutId, parsed);
      } else {
        editorInput.value = String(activeValue);
      }
    };

    editorInput.addEventListener("keydown", (event) => {
      event.stopPropagation();
      if (event.key === "Enter") {
        commitEditor();
        return;
      }
      if (event.key === "Escape") {
        editorInput.value = String(activeValue);
        editorInput.blur();
      }
    });
    editorInput.addEventListener("blur", () => requestAnimationFrame(commitEditor));
    editorInput.addEventListener("click", (event) => event.stopPropagation());

    let initialMarker: { layoutId: string; value: number; el: HTMLButtonElement } | null = null;

    orderedLayouts.forEach((layout, index) => {
      const segmentDiv = document.createElement("div");
      segmentDiv.setAttribute("data-stdg-panel-breakpoint-value", "");

      const labelSpan = document.createElement("span");
      labelSpan.textContent = layout.label;
      segmentDiv.appendChild(labelSpan);

      if (canDelete && index >= MIN_LAYOUTS) {
        const deleteBtn = makeDeleteButton(`Remove ${layout.label} layout`);
        deleteBtn.addEventListener("pointerdown", (event) => event.stopPropagation());
        deleteBtn.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.layouts.length > MIN_LAYOUTS) this.onRemoveLayout(layout.id);
        });
        segmentDiv.appendChild(deleteBtn);
      }

      breakpointsRow.appendChild(segmentDiv);

      if (index < orderedLayouts.length - 1) {
        const largerLayout = orderedLayouts[index + 1];
        const marker = document.createElement("button");
        marker.type = "button";
        marker.setAttribute("data-stdg-button", "");
        marker.setAttribute("data-stdg-panel-breakpoint-marker", "");
        marker.setAttribute("data-active", "false");
        marker.setAttribute("aria-label", `Edit ${largerLayout.label} breakpoint`);

        const markerSpan = document.createElement("span");
        markerSpan.textContent = String(largerLayout.minWidth);
        marker.appendChild(markerSpan);

        marker.addEventListener("click", (event) => {
          event.stopPropagation();
          setActive(largerLayout.id, largerLayout.minWidth, marker, true);
        });

        breakpointsRow.appendChild(marker);

        if (
          this.selectedBreakpointLayoutId === largerLayout.id ||
          (initialMarker === null && this.selectedBreakpointLayoutId === null)
        ) {
          initialMarker = { layoutId: largerLayout.id, value: largerLayout.minWidth, el: marker };
        }
      }
    });

    if (initialMarker !== null) {
      const m = initialMarker as { layoutId: string; value: number; el: HTMLButtonElement };
      setActive(m.layoutId, m.value, m.el);
    }

    return { breakpointsRow, inputField };
  }

  private syncChipRowStates(): void {
    const chips = this.layoutChipTabs.querySelectorAll("[data-stdg-panel-button][data-mode]");
    chips.forEach((chip) => {
      const mode = chip.getAttribute("data-mode");
      const layout = this.layouts.find((l) => l.label === mode);
      if (!layout) return;
      chip.setAttribute("data-active", String(layout.id === this.selectedLayoutId));
      chip.setAttribute("data-resolved", String(layout.id === this.resolvedLayoutId));
    });
  }

  private renderList(): void {
    const signature =
      this.instances
        .map((instance) => `${instance.id}:${instance.type}:${instance.visible}:${instance.name ?? ""}`)
        .join("|") + `#${this.selectedInstanceId ?? ""}`;
    if (signature === this.lastListSignature) return;
    this.lastListSignature = signature;

    while (this.addGuideButton.nextSibling) {
      this.listContainer.removeChild(this.addGuideButton.nextSibling);
    }
    for (const instance of this.instances) {
      const adapter = this.adapterRegistry.get(instance.type);
      if (!adapter) continue;
      const item = document.createElement("div");
      item.setAttribute("data-stdg-panel-list-item", "");
      item.setAttribute("data-stdg-button", "");
      item.setAttribute("data-stdg-button-toggle", "");
      item.setAttribute("data-active", String(instance.id === this.selectedInstanceId));
      item.innerHTML = `${resolveDevtoolsIcon(16, "grab", "second")}${adapter.icon.replace("<svg", "<svg data-stdg-icon-16")}<span>${instance.name?.trim() || adapter.label}</span>`;
      const toggle = this.createToggle(instance.visible, () => this.onToggle(instance.id));
      const deleteButton = makeDeleteButton(`Remove ${instance.name?.trim() || "instance"}`);
      deleteButton.addEventListener("click", (event) => {
        event.stopPropagation();
        if (this.selectedInstanceId === instance.id) this.closeDetailPanel();
        this.onRemove(instance.id);
      });
      item.appendChild(toggle);
      item.appendChild(deleteButton);
      item.addEventListener("click", () => {
        if (this.selectedInstanceId === instance.id) this.closeDetailPanel();
        else this.selectInstance(instance.id);
      });

      // Drag-and-drop reorder
      item.setAttribute("draggable", "true");
      item.addEventListener("dragstart", (event) => {
        event.stopPropagation();
        event.dataTransfer?.setData("text/plain", instance.id);
        item.setAttribute("data-dragging", "true");
      });
      item.addEventListener("dragend", () => {
        item.removeAttribute("data-dragging");
        this.listContainer
          .querySelectorAll<HTMLElement>("[data-drag-over]")
          .forEach((el) => el.removeAttribute("data-drag-over"));
      });
      item.addEventListener("dragover", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const rect = item.getBoundingClientRect();
        const placement = event.clientY < rect.top + rect.height / 2 ? "before" : "after";
        this.listContainer
          .querySelectorAll<HTMLElement>("[data-drag-over]")
          .forEach((el) => el.removeAttribute("data-drag-over"));
        item.setAttribute("data-drag-over", placement);
      });
      item.addEventListener("dragleave", (event) => {
        if (!item.contains(event.relatedTarget as Node)) {
          item.removeAttribute("data-drag-over");
        }
      });
      item.addEventListener("drop", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const draggedId = event.dataTransfer?.getData("text/plain");
        item.removeAttribute("data-drag-over");
        if (!draggedId || draggedId === instance.id) return;
        const rect = item.getBoundingClientRect();
        const placement: DropPlacement =
          event.clientY < rect.top + rect.height / 2 ? "before" : "after";
        this.onReorder(draggedId, instance.id, placement);
      });

      this.listContainer.appendChild(item);
    }
    this.addGuideButton.setAttribute("data-empty", String(this.instances.length === 0));
    const hasInstances = this.instances.length > 0;
    this.addGuideButton.style.display = hasInstances ? "none" : "";
    this.headerAddButton.style.display = hasInstances ? "" : "none";
  }

  private renderDetailPanel(): void {
    if (!this.selectedInstanceId) {
      this.hideDetailPanelUI();
      return;
    }
    const instance = this.instances.find((entry) => entry.id === this.selectedInstanceId);
    if (!instance) {
      this.clearSelection();
      return;
    }
    const adapter = this.adapterRegistry.get(instance.type);
    if (!adapter) {
      this.clearSelection();
      return;
    }
    if (this.detailIcon) {
      this.detailIcon.innerHTML = adapter.icon.replace(
        "<svg",
        "<svg data-stdg-icon-16 data-stdg-icon-second",
      );
    }
    if (this.detailTitle) this.detailTitle.textContent = instance.name?.trim() || adapter.label;
    this.detailBuilder?.destroy();
    this.detailBuilder = new GridUIBuilder();
    const settingsEl = this.detailBuilder.build(
      adapter.getUISchema(),
      instance.settings,
      (key, value) => this.onSettingChange(instance.id, key, value),
    );
    while (this.detailContent.firstChild) {
      this.detailContent.removeChild(this.detailContent.firstChild);
    }
    this.detailContent.appendChild(settingsEl);
    this.detailPanel.setAttribute("data-open", "true");
    this.alignDetailPanel();
    this.syncPanelPlacement();
  }

  private createToggle(checked: boolean, onChange: () => void): HTMLLabelElement {
    const label = document.createElement("label");
    label.setAttribute("data-stdg-toggle", "");
    label.setAttribute("data-checked", String(checked));
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = checked;
    input.setAttribute("data-stdg-toggle-input", "");
    input.addEventListener("change", (event) => {
      event.stopPropagation();
      onChange();
    });
    const knob = document.createElement("span");
    knob.setAttribute("data-stdg-toggle-knob", "");
    label.addEventListener("click", (event) => event.stopPropagation());
    label.appendChild(input);
    label.appendChild(knob);
    return label;
  }

  private startDetailRename(): void {
    if (!this.selectedInstanceId || !this.detailTitle) return;
    const instance = this.instances.find((entry) => entry.id === this.selectedInstanceId);
    if (!instance) return;
    const adapter = this.adapterRegistry.get(instance.type);
    if (!adapter) return;
    const currentName = instance.name?.trim() || adapter.label;
    const parent = this.detailTitle.parentElement;
    if (!parent) return;
    const input = document.createElement("input");
    input.setAttribute("data-stdg-input", "");
    input.type = "text";
    input.value = currentName;
    const commit = () => {
      const nextName = input.value.trim();
      if (nextName !== currentName) this.onRenameInstance(instance.id, nextName);
      if (parent.contains(input)) parent.replaceChild(this.detailTitle!, input);
    };
    input.addEventListener("blur", commit);
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") input.blur();
      if (event.key === "Escape") {
        input.value = currentName;
        input.blur();
      }
    });
    input.addEventListener("click", (event) => event.stopPropagation());
    input.addEventListener("pointerdown", (event) => event.stopPropagation());
    parent.replaceChild(input, this.detailTitle);
    input.focus();
    input.select();
  }

  private startLayoutTitleRename(): void {
    this.layoutTitle.select();
  }

  private openAddPanel(): void {
    this.isAddPanelOpen = true;
    this.closeLayoutPanel();
    this.clearSelection();
    this.renderAddPanelList();
    this.addPanel.setAttribute("data-open", "true");
    this.syncPanelPlacement();
  }

  private closeAddPanel(): void {
    this.isAddPanelOpen = false;
    this.addPanel.setAttribute("data-open", "false");
    this.syncPanelPlacement();
  }

  private openLayoutPanel(): void {
    this.isLayoutSettingsOpen = true;
    this.closeAddPanel();
    this.clearSelection();
    this.layoutPanel.setAttribute("data-open", "true");
    this.renderLayoutChips();
    this.alignLayoutPanel();
    this.syncPanelPlacement();
    this.onLayoutPanelOpen();
  }

  private closeLayoutPanel(): void {
    this.isLayoutSettingsOpen = false;
    this.layoutPanel.setAttribute("data-open", "false");
    this.renderLayoutChips();
    this.syncPanelPlacement();
    this.onLayoutPanelClose();
  }

  private alignLayoutPanel(): void {
    if (this.isMobilePanelMode()) {
      this.layoutPanel.style.removeProperty("top");
      return;
    }

    if (this.hudRoot.getAttribute("data-target-id") === "__global__") {
      this.layoutPanel.style.removeProperty("top");
      return;
    }

    const btnRect = this.layoutSettingsToggle.getBoundingClientRect();
    const containerRect = this.panel.getBoundingClientRect();
    this.layoutPanel.style.top = `${btnRect.top - containerRect.top}px`;
  }

  private selectInstance(instanceId: string): void {
    this.selectedInstanceId = instanceId;
    this.closeLayoutPanel();
    this.closeAddPanel();
    this.renderList();
    this.renderDetailPanel();
  }

  private closeDetailPanel(): void {
    this.clearSelection();
    this.renderList();
  }

  private clearSelection(): void {
    this.selectedInstanceId = null;
    this.hideDetailPanelUI();
  }

  private hideDetailPanelUI(): void {
    const builder = this.detailBuilder;
    this.detailBuilder = null;
    this.detailPanel.setAttribute("data-open", "false");
    this.syncPanelPlacement();
    window.setTimeout(() => {
      if (this.detailPanel.getAttribute("data-open") === "false") {
        builder?.destroy();
        this.detailContent.innerHTML = "";
        this.detailPanel.style.removeProperty("top");
      }
    }, 220);
  }

  private alignDetailPanel(): void {
    if (this.isMobilePanelMode()) {
      this.detailPanel.style.removeProperty("top");
      return;
    }

    if (this.hudRoot.getAttribute("data-target-id") === "__global__") {
      this.detailPanel.style.removeProperty("top");
      return;
    }

    const selectedItem = this.listContainer.querySelector(
      '[data-stdg-panel-list-item][data-active="true"]',
    ) as HTMLElement | null;
    if (!selectedItem) return;
    const itemRect = selectedItem.getBoundingClientRect();
    const containerRect = this.panel.getBoundingClientRect();
    this.detailPanel.style.top = `${itemRect.top - containerRect.top}px`;
  }

  public show(): void {
    this.isOpen = true;
    this.panel.setAttribute("data-open", "true");
    this.trigger.setAttribute("data-active", "true");
    this.trigger.setAttribute("aria-expanded", "true");
    this.syncPanelPlacement();
    this.onHUDOpen();
  }

  public hide(): void {
    this.isOpen = false;
    this.panel.setAttribute("data-open", "false");
    this.closeAddPanel();
    this.closeDetailPanel();
    this.closeLayoutPanel();
    this.trigger.setAttribute("data-active", "false");
    this.trigger.setAttribute("aria-expanded", "false");
    this.syncPanelPlacement();
    this.onHUDClose();
  }

  public toggle(): void {
    if (!this.enabled) return;
    if (this.isOpen) this.hide();
    else this.show();
  }

  private isMobilePanelMode(): boolean {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 1024px), (pointer: coarse)").matches;
  }

  private syncPanelPlacement(): void {
    const mobileMode = this.isMobilePanelMode();
    if (!mobileMode) {
      if (this.panelInBody) {
        this.panel.removeAttribute("data-mobile-sheet");
        this.panel.style.removeProperty("position");
        this.panel.style.removeProperty("bottom");
        this.hudRoot.appendChild(this.panel);
        this.panelInBody = false;
      }
      this.hudRoot.setAttribute("data-mobile-sheet", "false");
      this.panel.style.removeProperty("left");
      this.panel.style.removeProperty("top");
      this.panel.style.removeProperty("width");
      this.panel.style.removeProperty("height");
      this.panel.style.removeProperty("max-height");
      return;
    }

    if (!this.isOpen) {
      return;
    }

    if (!this.panelInBody) {
      (document.body ?? document.documentElement).appendChild(this.panel);
      this.panelInBody = true;
    }

    const viewport = window.visualViewport;
    const viewportWidth = viewport?.width ?? window.innerWidth;
    const viewportHeight = viewport?.height ?? window.innerHeight;
    const availableWidth = Math.max(220, viewportWidth - MOBILE_PANEL_MARGIN * 2);
    const availableHeight = Math.max(120, viewportHeight - MOBILE_PANEL_MARGIN * 2);
    const width = Math.min(MOBILE_PANEL_MAX_WIDTH, availableWidth);
    const maxHeight = Math.min(MOBILE_PANEL_MAX_HEIGHT, availableHeight);
    const left = Math.round((viewport?.offsetLeft ?? 0) + (viewportWidth - width) / 2);

    this.hudRoot.setAttribute("data-mobile-sheet", "true");
    this.panel.setAttribute("data-mobile-sheet", "true");
    this.panel.style.position = "fixed";
    this.panel.style.left = `${left}px`;
    this.panel.style.bottom = `${MOBILE_PANEL_MARGIN}px`;
    this.panel.style.removeProperty("top");
    this.panel.style.width = `${Math.round(width)}px`;
    this.panel.style.maxHeight = `${Math.round(maxHeight)}px`;
    this.panel.style.removeProperty("height");
    this.addPanel.style.removeProperty("top");
    this.layoutPanel.style.removeProperty("top");
    this.detailPanel.style.removeProperty("top");
  }

  private onDocumentPointerDown(event: PointerEvent): void {
    if (!this.isOpen) return;
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (
      this.panel.contains(target) ||
      this.trigger.contains(target) ||
      this.addPanel.contains(target) ||
      this.layoutPanel.contains(target) ||
      this.detailPanel.contains(target)
    ) {
      return;
    }
    this.hide();
  }
}
